/* Raceline poster option controls.

   "Configure your poster" shipped as four bare <select>s — correct, but
   nothing a customer can read at a glance. This replaces each one in place:

     Paper    → rows with the weight and what the finish actually looks like
     Frame    → colour swatches
     Solid    → the printable paper palette as swatches
     Track    → the matte-PLA palette as swatches
     Backdrop → tiles showing this poster's own renders

   The native <select> stays in the form and remains the single source of
   truth: every control writes back to it and fires `change`, so the
   line-item properties, the buy-buttons script and the gallery keep working
   untouched. Availability is not decided here — blocks/raceline-pdp-options
   hides the options the workshop cannot make, and a control mirrors whatever
   its select currently offers, labels included: buy-buttons rewrites those
   labels when its own request lands, so every pass re-reads them.

   Deliberately not touched: Horizon's variant picker. Horizon morphs that
   markup on every variant change and drops anything a script added there,
   which then has to be rebuilt a frame later — seen on the page as the whole
   option list reloading on each click. The size cards and colour dots are
   done with generated content in raceline-pdp.css instead, which a morph
   cannot strip. Keep it that way.

   Loaded (deferred) from blocks/raceline-pdp-options.liquid. */

(() => {
  const PAPER_NOTES = {
    matte: '200 gsm · flat, no glare',
    giclee: '300 gsm · deeper blacks, more texture'
  };

  const BACKDROP_NOTES = {
    map: 'The streets the route runs through',
    solid: 'One flat colour behind the line',
    flag: 'The country colours, as artwork'
  };

  // Frame timbers and finishes first, then the printable palettes shared with
  // the editor (solid papers and matte PLA).
  const FRAME_SWATCHES = {
    black: '#14100c', white: '#f6f3ec', oak: '#c69a5f', natural: '#d8b382',
    walnut: '#5a3a24', silver: '#c9c9c9', gold: '#c9a227'
  };

  const COLOUR_SWATCHES = {
    black: '#000000', white: '#FFFFFF', gray: '#9B9EA0', blue: '#0078BF',
    'light-blue': '#56B7E6', green: '#61C680', red: '#DE4343', orange: '#F99963',
    yellow: '#F7D959', purple: '#AE96D4', pink: '#E8AFCF', brown: '#7D6556',
    beige: '#E8DBB7', charcoal: '#000000', 'ivory-white': '#FFFFFF',
    'bone-white': '#CBC6B8', 'ash-gray': '#9B9EA0', 'nardo-gray': '#757575',
    'desert-tan': '#E8DBB7', 'latte-brown': '#D3B7A7', caramel: '#AE835B',
    terracotta: '#B15533', 'dark-brown': '#7D6556', 'dark-chocolate': '#4D3324',
    'lemon-yellow': '#F7D959', 'mandarin-orange': '#F99963', 'scarlet-red': '#DE4343',
    'dark-red': '#BB3D43', plum: '#950051', 'sakura-pink': '#E8AFCF',
    'lilac-purple': '#AE96D4', 'apple-green': '#C2E189', 'grass-green': '#61C680',
    'dark-green': '#68724D', 'ice-blue': '#A3D8E1', 'sky-blue': '#56B7E6',
    'marine-blue': '#0078BF', 'dark-blue': '#042F56'
  };

  const slug = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const labelOf = (option) => option.textContent.replace(/\s+/g, ' ').trim();

  // Writing an attribute that already holds the value still queues a mutation
  // record, which would wake this script and the filter over and over.
  function setAttr(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function swatchFor(kind, key) {
    if (kind === 'frame' && FRAME_SWATCHES[key]) return FRAME_SWATCHES[key];
    return COLOUR_SWATCHES[key] || FRAME_SWATCHES[key] || null;
  }

  function kindOf(select) {
    if (select.hasAttribute('data-rl-solid-color-property')) return 'solid';
    if (select.hasAttribute('data-rl-track-color-property')) return 'filament';
    if (select.hasAttribute('data-rl-frame-property')) return 'frame';
    if (select.hasAttribute('data-rl-background-property')) return 'backdrop';
    if (select.hasAttribute('data-rl-paper-property')) return 'paper';
    return 'plain';
  }

  // The three background renders already sit in the gallery; borrowing their
  // thumbnails beats inventing an icon for "Map".
  function backdropThumb(value) {
    const tabs = [...document.querySelectorAll('[data-rl-pdp-gallery-choice]')];
    const match = tabs.find((tab) => slug((tab.dataset.label || '').split('·')[0]) === slug(value));
    return match?.querySelector('img')?.currentSrc || match?.dataset.src || null;
  }

  function buildItem(kind, option) {
    const key = slug(option.value);
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'rl-choice__item';
    item.setAttribute('role', 'radio');
    item.dataset.value = option.value;

    const swatch = kind === 'frame' || kind === 'solid' || kind === 'filament' ? swatchFor(kind, key) : null;
    if (swatch) {
      const mark = document.createElement('span');
      mark.className = 'rl-choice__swatch';
      mark.style.setProperty('--rl-swatch', swatch);
      mark.setAttribute('aria-hidden', 'true');
      item.appendChild(mark);
    }

    if (kind === 'backdrop') {
      const thumb = backdropThumb(option.value);
      const frame = document.createElement('span');
      frame.className = 'rl-choice__thumb';
      frame.setAttribute('aria-hidden', 'true');
      if (thumb) {
        const image = document.createElement('img');
        image.src = thumb;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        frame.appendChild(image);
      }
      item.appendChild(frame);
    }

    const body = document.createElement('span');
    body.className = 'rl-choice__body';
    const name = document.createElement('span');
    name.className = 'rl-choice__name';
    name.textContent = labelOf(option);
    body.appendChild(name);

    const note = kind === 'paper' ? PAPER_NOTES[/gicl/i.test(option.value) ? 'giclee' : 'matte']
      : kind === 'backdrop' ? BACKDROP_NOTES[key]
      : null;
    if (note) {
      const line = document.createElement('span');
      line.className = 'rl-choice__note';
      line.textContent = note;
      body.appendChild(line);
    }
    item.appendChild(body);

    if (kind === 'paper' || kind === 'backdrop') {
      const tick = document.createElement('span');
      tick.className = 'rl-choice__tick';
      tick.setAttribute('aria-hidden', 'true');
      item.appendChild(tick);
    }

    // A swatch grid has no room for a caption, so the name has to reach
    // assistive tech some other way.
    if ((kind === 'solid' || kind === 'filament') && swatch) {
      body.classList.add('rl-choice__body--hidden');
    }

    return item;
  }

  function select(control, value) {
    const field = control.rlSelect;
    if (!field || field.value === value) return;
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function enhance(field) {
    const holder = field.closest('label') || field.parentElement;
    if (!holder) return null;
    const kind = kindOf(field);
    const signature = [...field.options].map((option) => option.value).join('|');
    let control = holder.querySelector(':scope > .rl-choice');

    if (control && control.dataset.signature === signature) return control;
    if (control) control.remove();

    control = document.createElement('div');
    control.className = 'rl-choice rl-choice--' + kind;
    control.dataset.signature = signature;
    control.setAttribute('role', 'radiogroup');
    control.rlSelect = field;
    const caption = holder.querySelector('span');
    if (caption) control.setAttribute('aria-label', caption.textContent.trim());

    [...field.options].forEach((option) => control.appendChild(buildItem(kind, option)));

    control.addEventListener('click', (event) => {
      const item = event.target.closest('.rl-choice__item');
      if (item) select(control, item.dataset.value);
    });

    // Roving focus, so a palette of twenty-five swatches is one tab stop
    // rather than twenty-five.
    control.addEventListener('keydown', (event) => {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const items = [...control.querySelectorAll('.rl-choice__item')].filter((item) => !item.hidden);
      const index = items.indexOf(event.target.closest('.rl-choice__item'));
      if (index < 0 || !items.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      const target = event.key === 'Home' ? items[0]
        : event.key === 'End' ? items[items.length - 1]
        : items[(index + step + items.length) % items.length];
      select(control, target.dataset.value);
      target.focus();
    });

    if (!field.classList.contains('rl-fieldswap')) field.classList.add('rl-fieldswap');
    setAttr(field, 'tabindex', '-1');
    setAttr(field, 'aria-hidden', 'true');
    holder.appendChild(control);
    return control;
  }

  function sync(control) {
    const field = control.rlSelect;
    if (!field) return;
    const chosen = field.value;
    let name = '';
    control.querySelectorAll('.rl-choice__item').forEach((item) => {
      const option = [...field.options].find((entry) => entry.value === item.dataset.value);
      const gone = !option || option.hidden;
      if (item.hidden !== gone) item.hidden = gone;
      if (option) {
        // Re-read the label every pass: buy-buttons rewrites its own option
        // text when its availability request lands, and the filter strips that
        // again afterwards.
        const text = labelOf(option);
        const printed = item.querySelector('.rl-choice__name');
        if (printed && printed.textContent !== text) printed.textContent = text;
        if (item.querySelector('.rl-choice__body--hidden')) {
          setAttr(item, 'aria-label', text);
          if (item.title !== text) item.title = text;
        }
      }
      const active = !gone && option.value === chosen;
      setAttr(item, 'aria-checked', String(active));
      const tab = active ? 0 : -1;
      if (item.tabIndex !== tab) item.tabIndex = tab;
      if (active) name = labelOf(option);
    });
    if (!control.querySelector('[aria-checked="true"]')) {
      const first = [...control.querySelectorAll('.rl-choice__item')].find((item) => !item.hidden);
      if (first && first.tabIndex !== 0) first.tabIndex = 0;
    }

    // Echo the chosen value beside the group label — a swatch grid otherwise
    // never names what is selected.
    const holder = control.parentElement;
    const caption = holder?.querySelector('span:not(.rl-optvalue)');
    if (caption) {
      let echo = caption.querySelector('.rl-optvalue');
      if (!echo) {
        echo = document.createElement('span');
        echo.className = 'rl-optvalue';
        caption.appendChild(echo);
      }
      if (echo.textContent !== name) echo.textContent = name;
    }
  }

  function run() {
    try {
      document.querySelectorAll('fieldset[data-rl-native-finish] select').forEach((field) => {
        const control = enhance(field);
        if (control) sync(control);
      });
    } catch (error) {
      console.warn('[raceline] option controls failed', error);
    }
  }

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    // setTimeout rather than requestAnimationFrame: background tabs never run
    // rAF, and a stuck latch would freeze every later sync.
    setTimeout(() => {
      queued = false;
      run();
    }, 40);
  }

  // Only the poster fieldset is watched. Watching the whole document would
  // wake this on every variant morph for nothing.
  const scope = document.querySelector('fieldset[data-rl-native-finish]')?.closest('product-form-component') || document.body;
  new MutationObserver(schedule).observe(scope, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'disabled', 'value']
  });
  document.addEventListener('change', schedule);
  document.addEventListener('rl:background-change', schedule);
  window.addEventListener('load', schedule);
  [400, 1500].forEach((delay) => setTimeout(schedule, delay));

  run();
})();
